// SocialIcons — sidebar row of social-presence links on the public
// profile.
//
// M7-socials (2026-06-07). Renders one icon per non-null URL among
// Instagram / YouTube / Facebook. LinkedIn is intentionally NOT in
// the set per Kaillum's 2026-06-07 call:
//   "we're trying to differentiate ourselves from LinkedIn."
// Don't add LinkedIn here even if a future copy edit asks for "more
// platforms" — it's a product decision. New platforms (TikTok, X)
// would each need a schema column, then an icon component below.
//
// lucide-react doesn't ship brand-trademark icons (Instagram /
// Facebook / YouTube are owned trademarks; the upstream Lucide
// project removed them years ago). Each icon is inlined as a small
// SVG component below — single-path designs that inherit `text-*`
// classes via `fill="currentColor"`. The paths are the standard
// glyphs used across the web for these platforms; no original art.
//
// The row hides entirely when all three URLs are null — matches the
// sidebar pattern of self-hiding empty cards.

import type { ReactNode } from 'react'

interface Props {
  instagramUrl: string | null
  youtubeUrl: string | null
  facebookUrl: string | null
}

export default function SocialIcons({
  instagramUrl,
  youtubeUrl,
  facebookUrl,
}: Props) {
  const links: Array<{ url: string; label: string; icon: ReactNode }> = []
  if (instagramUrl) {
    links.push({
      url: instagramUrl,
      label: 'Instagram',
      icon: <InstagramIcon />,
    })
  }
  if (youtubeUrl) {
    links.push({
      url: youtubeUrl,
      label: 'YouTube',
      icon: <YouTubeIcon />,
    })
  }
  if (facebookUrl) {
    links.push({
      url: facebookUrl,
      label: 'Facebook',
      icon: <FacebookIcon />,
    })
  }
  if (links.length === 0) return null

  return (
    <section
      aria-label="Social links"
      className="bg-hoop-surface border border-hoop-border rounded-2xl p-4"
    >
      <ul className="flex items-center gap-3">
        {links.map((l) => (
          <li key={l.label}>
            <a
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={l.label}
              title={l.label}
              className="inline-flex items-center justify-center w-9 h-9 rounded-md text-hoop-text-2 hover:text-hoop-accent hover:bg-hoop-bg transition-colors"
            >
              {l.icon}
            </a>
          </li>
        ))}
      </ul>
    </section>
  )
}

// 20×20 viewBox, single-path glyphs. fill="currentColor" inherits the
// surrounding text color so the hover state (text-hoop-accent etc.)
// recolors the icon without any additional CSS.

function InstagramIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  )
}

function YouTubeIcon() {
  // Stylized play-in-rounded-rectangle. Stroke-based to match the
  // Instagram glyph's visual weight rather than a fill-only block.
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z" />
      <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02" fill="currentColor" />
    </svg>
  )
}

function FacebookIcon() {
  // Classic "f" mark inside the rounded square. Stroke variant for
  // visual parity with the other two glyphs.
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  )
}
